import Glyph from "@/components/Glyph";
import { isNative, registerPush } from "@/lib/native";
import ShareAstra from "@/components/ShareAstra";
import StreakBadges from "@/components/StreakBadges";
import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { api, clearAuthToken } from "@/lib/apiClient";
import { Crown, LogOut, Share, Download, Bell, Trash2, Shield, LifeBuoy, Globe, Loader2, Gift, RotateCcw } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Profile({ onSubscribe }) {
  const { user, logout, refresh, setUser } = useAuth();
  const { currency, prices, changeCurrency } = useCurrency();
  const navigate = useNavigate();
  const [reminders, setReminders] = useState(user?.reminders_enabled ?? true);
  const [hour, setHour] = useState(user?.reminder_hour ?? 8);
  const [push, setPush] = useState(user?.push_enabled ?? true);
  const [retro, setRetro] = useState(user?.retro_alerts ?? true);
  const [saving, setSaving] = useState(false);
  if (!user) return null;
  const t = user.trinity;

  const toggleRetro = async () => {
    const next = !retro;
    setRetro(next);
    try { await api.patch("/profile/reminders", { retro_alerts: next }); await refresh(); } catch { setRetro(!next); }
  };

  const patchReminders = async (enabled, h, pushOn = push) => {
    setSaving(true);
    try {
      await api.patch("/profile/reminders", { enabled, hour: h, push_enabled: pushOn });
      await refresh();
    } finally {
      setSaving(false);
    }
  };

  const toggleReminders = async () => {
    const next = !reminders;
    setReminders(next);
    await patchReminders(next, hour).catch(() => setReminders(!next));
  };

  const togglePush = async () => {
    const next = !push;
    setPush(next);
    await patchReminders(reminders, hour, next).catch(() => setPush(!next));
  };

  const changeHour = async (h) => {
    setHour(h);
    await patchReminders(reminders, h);
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/profile");
      clearAuthToken();
    } finally {
      setUser(false);
      navigate("/");
    }
  };

  const label12 = (h) => {
    const ampm = h < 12 ? "am" : "pm";
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr}:00 ${ampm}`;
  };

  return (
    <div className="px-5 pt-5 fade-up">
      <div className="flex flex-col items-center text-center mb-6">
        <div className="mb-3">
          <img src="/astra-mark-128.png" alt="Astra" data-testid="profile-astra-logo" className="w-20 h-20" />
        </div>
        <h1 className="text-3xl font-light">{user.name}</h1>
        <p className="text-sm text-[#A7B0C0]">{user.email}</p>
        {t && (
          <p className="text-base text-[#E8DCC4] mt-1">
            <Glyph ch={t.sun.glyph} /> {t.sun.sign} · <Glyph ch={t.moon.glyph} /> {t.moon.sign} · ↑ {t.rising.sign}
          </p>
        )}
      </div>

      <div className={`rounded-2xl p-5 mb-4 border ${user.is_premium ? "border-[#D4AF37]/40 bg-[#0F1523]" : "border-[#D4AF37]/15 bg-[#0F1523]"}`}>
        <div className="flex items-center gap-2 mb-1">
          <Crown size={18} className="gold-text" />
          <p className="text-xl">{user.is_premium ? "Astra Premium" : "Free Reader"}</p>
        </div>
        {user.is_premium ? (
          <p className="text-sm text-[#A7B0C0]">You have full access to every reading. The whole sky is yours.</p>
        ) : (
          <>
            <p className="text-sm text-[#A7B0C0] mb-3">Unlock Love Focus, Money, Weekly & Monthly forecasts from {prices.symbol}{prices.annualPerMonth}/mo.</p>
            {!isNative && <button
              data-testid="btn-subscribe-profile"
              onClick={() => onSubscribe("premium")}
              className="w-full bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium py-3 rounded-full transition-colors"
            >
              Upgrade to Premium
            </button>}
          </>
        )}
      </div>

      {!isNative && (
      <div className="bg-[#0F1523] border border-[#D4AF37]/15 rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-2">
            <Globe size={16} className="gold-text mt-0.5" />
            <div>
              <p className="text-lg">Currency</p>
              <p className="text-sm text-[#A7B0C0]">Prices shown and charged in this currency.</p>
            </div>
          </div>
          <select
            data-testid="select-currency"
            value={currency}
            onChange={(e) => changeCurrency(e.target.value)}
            className="bg-[#04060B] border border-[#D4AF37]/25 rounded-xl px-3 py-2 text-base text-starlight focus:outline-none focus:border-[#D4AF37]/60 cursor-pointer"
          >
            <option value="usd">$ USD</option>
            <option value="eur">€ EUR</option>
            <option value="gbp">£ GBP</option>
          </select>
        </div>
      </div>
      )}

      <div className="bg-[#0F1523] border border-[#D4AF37]/15 rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-2">
            <Bell size={16} className="gold-text mt-0.5" />
            <div>
              <p className="text-lg">Email reminder</p>
              <p className="text-sm text-[#A7B0C0]">A gentle email to read today's stars.</p>
            </div>
          </div>
          <button
            data-testid="toggle-reminders"
            role="switch"
            aria-checked={reminders}
            onClick={toggleReminders}
            disabled={saving}
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${reminders ? "bg-[#D4AF37]" : "bg-[#2a3142]"}`}
          >
            <span
              className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#04060B] transition-transform ${reminders ? "translate-x-5" : ""}`}
            />
          </button>
        </div>
        {isNative && (
          <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-[#D4AF37]/10">
            <div className="flex items-start gap-2">
              <Bell size={16} className="gold-text mt-0.5" />
              <div>
                <p className="text-lg">Phone notification</p>
                <p className="text-sm text-[#A7B0C0]">A daily nudge on your iPhone when your reading is ready.</p>
              </div>
            </div>
            <button
              data-testid="toggle-push"
              role="switch"
              aria-checked={push}
              onClick={togglePush}
              disabled={saving}
              className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${push ? "bg-[#D4AF37]" : "bg-[#2a3142]"}`}
            >
              <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#04060B] transition-transform ${push ? "translate-x-5" : ""}`} />
            </button>
          </div>
        )}
        {isNative && push && <PushTestButton />}
        <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-[#D4AF37]/10">
          <div className="flex items-start gap-2">
            <RotateCcw size={16} className="gold-text mt-0.5" />
            <div>
              <p className="text-lg">Retrograde alerts</p>
              <p className="text-sm text-[#A7B0C0]">A heads-up 3 days before Mercury, Venus or Mars turns retrograde, and when it ends.</p>
            </div>
          </div>
          <button data-testid="toggle-retro-alerts" role="switch" aria-checked={retro} onClick={toggleRetro}
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${retro ? "bg-[#D4AF37]" : "bg-[#2a3142]"}`}>
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-[#04060B] transition-transform ${retro ? "translate-x-5" : ""}`} />
          </button>
        </div>
        {(reminders || (isNative && push)) && (
          <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-[#D4AF37]/10">
            <p className="text-base text-[#D6D0C5]">Send it at</p>
            <select
              data-testid="select-reminder-hour"
              value={hour}
              onChange={(e) => changeHour(Number(e.target.value))}
              disabled={saving}
              className="bg-[#04060B] border border-[#D4AF37]/25 rounded-xl px-3 py-2 text-base text-starlight focus:outline-none focus:border-[#D4AF37]/60 cursor-pointer"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{label12(i)}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!isNative && <div className="bg-[#0F1523] border border-[#D4AF37]/15 rounded-2xl p-5 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Download size={16} className="gold-text" />
          <p className="text-lg">Install on your iPhone</p>
        </div>
        <p className="text-sm text-[#A7B0C0] flex items-center gap-1 flex-wrap">
          In Safari, tap <Share size={14} className="inline gold-text" /> then
          <span className="gold-text">Add to Home Screen</span> for a full-screen, offline daily ritual.
        </p>
      </div>}

      {!user.is_demo && <StreakBadges />}
      {!user.is_demo && <ShareAstra className="mb-4" />}
      {!user.is_demo && !isNative && (
        <button data-testid="link-gifts" onClick={() => navigate("/gifts")}
          className="w-full mb-4 flex items-center justify-between bg-[#0F1523] border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 rounded-2xl px-5 py-4 transition-colors text-left">
          <span className="flex items-center gap-2 text-[#E8DCC4]"><Gift size={16} className="gold-text" /> Gift a reading to a friend</span>
          <span className="text-xs gold-text">Choose the day it arrives</span>
        </button>
      )}

      <button
        data-testid="btn-logout"
        onClick={logout}
        className="w-full flex items-center justify-center gap-2 border border-[#D4AF37]/20 text-[#A7B0C0] hover:text-starlight py-3 rounded-full transition-colors"
      >
        <LogOut size={16} /> Sign out
      </button>

      <div className="mt-6 pt-5 border-t border-[#D4AF37]/10 flex flex-col items-center gap-4">
        <button
          data-testid="link-privacy"
          onClick={() => navigate("/privacy")}
          className="flex items-center gap-1.5 text-sm text-[#A7B0C0] hover:text-[#E8DCC4] transition-colors"
        >
          <Shield size={14} /> Privacy Policy
        </button>
        <button
          data-testid="link-support"
          onClick={() => navigate("/support")}
          className="flex items-center gap-1.5 text-sm text-[#A7B0C0] hover:text-[#E8DCC4] transition-colors"
        >
          <LifeBuoy size={14} /> Help & Support
        </button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              data-testid="btn-delete-account"
              className="flex items-center gap-1.5 text-sm text-red-300/80 hover:text-red-300 transition-colors"
            >
              <Trash2 size={14} /> Delete my account
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#0B0F19] border border-[#D4AF37]/25 text-starlight max-w-[22rem] rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-starlight font-serif text-2xl font-light">Delete your account?</AlertDialogTitle>
              <AlertDialogDescription className="text-[#A7B0C0] font-serif text-base">
                This permanently removes your profile, birth chart, saved readings and purchase history. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-transparent border border-[#D4AF37]/25 text-[#E8DCC4] hover:bg-[#D4AF37]/10 hover:text-starlight rounded-full">
                Keep my account
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="btn-confirm-delete"
                onClick={deleteAccount}
                className="bg-red-500/90 hover:bg-red-500 text-white rounded-full"
              >
                Delete forever
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

const PUSH_MSG = {
  denied: "Notifications are turned off for Astra. Allow them in iPhone Settings, then Notifications, then Astra.",
  error: "Your iPhone couldn't register for notifications. Please try again.",
  timeout: "Your iPhone didn't answer in time. Please try again.",
};

function PushTestButton() {
  const [busy, setBusy] = useState(false);
  const test = async () => {
    setBusy(true);
    try {
      const reg = await registerPush();
      if (PUSH_MSG[reg]) return toast.error(PUSH_MSG[reg]);
      const { data } = await api.post("/push/test");
      if (data.sent) toast.success("Test notification sent", { description: "It should appear on your iPhone in a few seconds." });
      else toast.error(data.reason || "The notification couldn't be sent.");
    } catch {
      toast.error("The notification couldn't be sent. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      data-testid="btn-push-test"
      onClick={test}
      disabled={busy}
      className="mt-3 w-full flex items-center justify-center gap-2 border border-[#D4AF37]/40 gold-text py-2.5 rounded-full hover:border-[#D4AF37] transition-colors disabled:opacity-60"
    >
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Bell size={15} />} Send test notification
    </button>
  );
}
